import React from "react";
import styled from "styled-components";
import { breakPoints, mediaMaxWidth, postGroteskFont } from "./Shared";

// Switch to mobile view below 600 px
const mobileBreakpoint = mediaMaxWidth(599);

const cardFrame = {
    minHeight: 600,
    maxWidth: 1280
};

const card = {
    borderRadius: 18,
    desktop: {
        gap: 80,
        height: 650,
        padding: 24,
        flexBasis: `calc((100% - 8px) / 2)`,
    },
    lg: {
        gap: 64,
    },
    sm: {
        gap: 0,
        height: 919
    },
    mobile: {
        minHeight: 763,
        padding: 0,
        paddingTop: 24,
    }
};

const contentPanel = {
    desktop: {
        gap: 0,
        paddingY: 64,
        paddingLeft: 32,
        paddingRight: 32,
        paddingX: 32,
        minWidth: 274,
        flexBasis: `calc((100% - ${card.desktop.gap}px) / 2)`
    },
    sm: {
        height: 521,
        paddingY: 64,
        paddingRight: 32,
        paddingX: 0,
    },
    mobile: {
        padding: 24,
    }
};

const contentFrame = {
    desktop: {
        gap: 32,
    },
    mobile: {
        gap: 24
    }
};

const contentHeader = {
    desktop: {
        gap: 24,
    },
    mobile: {
        gap: 10,
    },
};

const headingText = {
    desktop: {
        fontSize: 37,
        lineHeight: 1.28
    },
    mobile: {
        fontSize: 28,
        lineHeight: 1.3
    },
    maxWidth: 523
};

const descriptionText = {
    desktop: {
        fontSize: 20,
        lineHeight: 1.45
    },
    mobile: {
        fontSize: 16,
        lineHeight: 1.4
    }
};

const imageFrame = {
    borderRadius: 12,
    container: {
        desktopHeight: 592,
        stackHeight: 350,
        mobileHeight: 320
    },
    viewport: {
        desktopHeight: 592,
        desktopWidth: 956,
        mobileHeight: 396.937,
        mobileWidth: 641
    },
    image: {
        height: 592,
        width: 576
    }
};

export const CardFrame = styled.div<{ $fullWidth?: boolean }>`
    align-items: flex-start;
    display: flex;
    justify-content: center;
    min-height: ${cardFrame.minHeight}px;
    position: relative;
    width: ${({ $fullWidth }) => $fullWidth ? "100%" : `unset`};
    max-width: ${cardFrame.maxWidth}px;
    
    ${mobileBreakpoint`
        min-height: 0;
    `}
`;

export const Card = styled.section`
    align-items: center;
    align-self: stretch;
    background: #ffffff;
    border-radius: ${card.borderRadius}px;
    display: flex;
    min-height: ${card.desktop.height}px;
    justify-content: space-between;
    overflow: hidden;
    padding: ${card.desktop.padding}px;
    position: relative;
    width: 100%;
    z-index: 1;
    
    ${breakPoints.sm`
        flex-direction: column;
        min-height: ${card.sm.height}px;
    `}

    ${mobileBreakpoint`
        min-height: 0;
        height: auto;
        justify-content: flex-start;
        padding: ${card.mobile.padding}px;
        padding-top: ${card.mobile.paddingTop}px;         
    `}
`;

export const CardSpacer = styled.div`
    flex: 0 1 ${card.desktop.gap};
`;

export const ContentPanel = styled.div`
    align-items: flex-start;
    display: flex;
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: ${contentPanel.desktop.flexBasis};
    flex-direction: column;
    gap: ${contentPanel.desktop.gap}px;
    justify-content: flex-start;
    height: auto;
    min-height: calc(${card.desktop.height}px - ${card.desktop.padding * 2}px);
    min-width: 274px;
    padding-top: ${contentPanel.desktop.paddingY}px;
    padding-bottom: ${contentPanel.desktop.paddingY}px;
    padding-left: ${contentPanel.desktop.paddingLeft}px;
    padding-right: ${contentPanel.desktop.paddingRight}px;
    position: relative;

    ${breakPoints.sm`
        flex: 0 0 auto;
        height: auto;
        min-height: ${contentPanel.sm.height}px;
        padding-top: ${contentPanel.sm.paddingY}px;
        padding-bottom: ${contentPanel.sm.paddingY}px;
        padding-right: ${contentPanel.sm.paddingRight}px;
        width: 100%;
    `}

    ${mobileBreakpoint`
        min-height: 0;
        padding: ${contentPanel.mobile.padding}px;
    `}
`;

export const ContentFrame = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: ${contentFrame.desktop.gap}px;
    position: relative;
    width: 100%;

    ${breakPoints.md`
        gap: ${contentFrame.mobile.gap}px;
    
    `}

    ${mobileBreakpoint`
        gap: ${contentFrame.mobile.gap}px;
    
    `}
`;

export const ContentHeader = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: ${contentHeader.desktop.gap}px;
    position: relative;
    width: 100%;

    ${mobileBreakpoint`
        gap: ${contentHeader.mobile.gap}px;
    `}
`;

export const HeadingText = styled.h1`
    color: #000000;
    font-family: ${postGroteskFont};
    font-size: ${headingText.desktop.fontSize}px;
    font-weight: 500;
    letter-spacing: 0;
    line-height: ${headingText.desktop.lineHeight};
    margin: 0;
    max-width: ${headingText.maxWidth}px;
    width: 100%;

    ${mobileBreakpoint`
        font-size: ${headingText.mobile.fontSize}px;
        line-height: ${headingText.mobile.lineHeight};
    `}
`;

export const EmphasisText = styled.span.attrs({ "data-test-id": "tour-card-emphasis-text" })`
    background: linear-gradient(290.63deg, #db2475 11.35%, #00b9ff 90.94%);
    background-clip: text;
    color: transparent;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline;
`;

export const DescriptionText = styled.p.attrs({ "data-test-id": "tour-card-description-text" })`
    color: #000000;
    font-family: ${postGroteskFont};
    font-size: ${descriptionText.desktop.fontSize}px;
    font-weight: 400;
    line-height: ${descriptionText.desktop.lineHeight};
    margin: 0;
    width: 100%;

    ${mobileBreakpoint`
        font-size: ${descriptionText.mobile.fontSize}px;
        line-height: ${descriptionText.mobile.lineHeight};
    `}
`;

const ProgressTracker = styled.div.attrs({ "data-test-id": "tour-card-progress-tracker" })`
    background: #eeeeee;
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: 6px;
    height: 6px;
    overflow: hidden;
    position: relative;
    width: 100%;
`;

const StyledProgressBar = styled.div.attrs({ "data-test-id": "tour-card-progress-bar" }) <{ $progress: number }>`
    background: #00b9ff;
    bottom: 0;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $progress }) => $progress}%;
    transition: width 300ms ease;
`;

export const ProgressSpacer = styled.div`
    flex: 1 1 80px;
    min-height: 24px;
    max-height: 80px;
    width: 100%;

    ${mobileBreakpoint`
        max-height: 24px;
    `}

`;

const ImageFrame = styled.div`
    border-radius: ${imageFrame.borderRadius}px;
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: ${contentPanel.desktop.flexBasis};
    height: 100%;
    min-width: 0;
    overflow: hidden;
    position: relative;

    ${breakPoints.sm`
        flex: 0 0 auto;
        height: ${imageFrame.container.stackHeight}px;
        width: 100%;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    `}

    ${mobileBreakpoint`
        height: ${imageFrame.container.mobileHeight}px;
    `}
`;

const Image = styled.img<{ imagePosition: string }>`
    display: block;
    height: 100%;
    left: 0;
    max-width: none;
    object-fit: cover;
    object-position: ${({ imagePosition }) => imagePosition};
    
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
`;

export function ProgressBar({ progress = 0 }: { progress?: number }) {
    return (
        <ProgressTracker aria-hidden="true">
            <StyledProgressBar $progress={progress} />
        </ProgressTracker>
    );
}

export function ImagePanel({ image, imagePosition = "left center" }: { image: string, imagePosition: string }) {
    return (
        <ImageFrame aria-hidden="true">
            <Image alt="" src={image} imagePosition={imagePosition} />
        </ImageFrame>
    );
}
